// Public Pricing React Components
// Simplified version of billing components for public pricing page

const { useState, useEffect } = React;

// Free-endpoint rate limits per plan (requests / minute).
// Mirrors FREE_API_LIMITS_BY_PLAN in src/py/services/platform/rate_limit_service.py.
const PUBLIC_FREE_API_RATE_LIMITS = {
  starter: 20,
  growth: 50,
  pro: 100,
  ultra: 300
};
const PUBLIC_FREE_API_RATE_LIMIT_DEFAULT = 2; // no active subscription (PAYG)

// ==================== Public Plans Section Component ====================

const PublicPlansSection = ({ 
  activeTab, 
  setActiveTab, 
  selectedPlan, 
  setSelectedPlan, 
  selectedPayg, 
  setSelectedPayg, 
  isAnnual, 
  setIsAnnual, 
  plans, 
  paygPlans
}) => {
  // Helper functions - exact same as dashboard
  const calculateCostPerCredit = (price, credits) => {
    const priceNum = parseFloat(price.replace('$', '').replace(',', ''));
    const creditsNum = parseFloat(credits.replace(/,/g, ''));
    const costPerCredit = priceNum / creditsNum;
    
    // Format based on the magnitude of the value
    if (costPerCredit < 0.01) {
      return costPerCredit.toFixed(5);
    } else if (costPerCredit < 0.1) {
      return costPerCredit.toFixed(4);
    } else {
      return costPerCredit.toFixed(2);
    }
  };

  const calculateCPM = (price, credits) => {
    const priceNum = parseFloat(price.replace('$', '').replace(',', ''));
    const creditsNum = parseFloat(credits.replace(',', ''));
    return ((priceNum / creditsNum) * 1000).toFixed(2);
  };

  const calculateCapabilityUses = (credits, creditCost) => {
    const creditsNum = parseFloat(credits.replace(',', ''));
    return Math.floor(creditsNum / creditCost).toLocaleString();
  };

  // Get the current plan details for the right panel
  const getCurrentPlanDetails = () => {
    if (activeTab === 'subscription') {
      const plan = plans[selectedPlan] || plans['starter'];
      if (!plan) {
        return {
          name: 'Starter',
          monthlyPrice: '$49',
          annualPrice: '$588',
          monthlyCredits: '4,000',
          annualCredits: '4,800',
          displayPrice: '$49',
          displayCredits: '4,000',
          credits: '4,000',
          costPerCredit: '0.0123',
          cpm: '12.30'
        };
      }
      const displayPrice = isAnnual ? plan.annualPrice : plan.monthlyPrice;
      const displayCredits = isAnnual ? plan.annualCredits : plan.monthlyCredits;
      
      // Handle Enterprise plan special cases
      if (displayPrice === 'Contact Sales' || displayCredits === 'Custom') {
        return {
          ...plan,
          displayPrice,
          displayCredits,
          credits: displayCredits,
          costPerCredit: 'Contact Sales',
          cpm: 'Contact Sales'
        };
      }
      
      const costPerCredit = calculateCostPerCredit(displayPrice, displayCredits);
      const cpm = calculateCPM(displayPrice, displayCredits);
      return { ...plan, displayPrice, displayCredits, credits: displayCredits, costPerCredit, cpm };
    } else {
      const plan = paygPlans[selectedPayg] || paygPlans['3788'];
      if (!plan) {
        return {
          price: '$100',
          credits: '3,788',
          costPerCredit: '0.0264',
          cpm: '26.40'
        };
      }
      const costPerCredit = calculateCostPerCredit(plan.price, plan.credits);
      const cpm = calculateCPM(plan.price, plan.credits);
      return { ...plan, costPerCredit, cpm };
    }
  };

  const currentPlan = getCurrentPlanDetails();

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Trial Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
            <span className="font-semibold text-lg">Start with a 3-Day Free Trial</span>
          </div>
          <span className="text-purple-100">•</span>
          <span className="text-sm">10 credits included • All features unlocked • No credit card required</span>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row">
        {/* Left Column: Tabs and Content */}
        <div className="w-full lg:w-2/3 border-r border-zinc-200">
          {/* Tab Buttons */}
          <div className="flex border-b border-zinc-200">
            <button
              onClick={() => setActiveTab('subscription')}
              className={`w-1/2 py-4 px-6 text-center font-medium focus:outline-none transition-colors duration-150 ease-in-out ${
                activeTab === 'subscription' 
                  ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500' 
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              <div>
                <div className="font-medium">Annual Subscription</div>
                <div className="text-xs mt-1 font-normal opacity-75">
                  {isAnnual 
                    ? 'Credits issued immediately, expire in a year'
                    : 'Monthly credits issued monthly, expire every month'
                  }
                </div>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('payg')}
              className={`w-1/2 py-4 px-6 text-center font-medium focus:outline-none transition-colors duration-150 ease-in-out ${
                activeTab === 'payg'
                  ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              <div>
                <div className="font-medium">Pay As You Go</div>
                <div className="text-xs mt-1 font-normal opacity-75">
                  90-day validity for packs shown (extends with purchases)
                </div>
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6 lg:p-8">
            {activeTab === 'subscription' ? (
              <div>
                {/* Billing Toggle */}
                <div className="flex items-center justify-center mb-8 space-x-3 flex-wrap">
                  <span className={`text-xs sm:text-sm text-center transition-colors duration-200 ${isAnnual ? 'text-zinc-500' : 'text-zinc-900 font-medium'}`}>
                    <span className="hidden xl:inline">Annual plan, Paid monthly</span>
                    <span className="xl:hidden">Annual plan,<br />Paid monthly</span>
                  </span>
                  <button
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 ${
                      isAnnual ? 'bg-purple-600' : 'bg-zinc-200'
                    }`}
                    onClick={() => setIsAnnual(!isAnnual)}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        isAnnual ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className={`text-xs sm:text-sm text-center transition-colors duration-200 ${isAnnual ? 'text-zinc-900 font-medium' : 'text-zinc-500'}`}>
                    <span className="hidden xl:inline">
                      Annual plan, Paid annually{' '}
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">+2 MONTHS FREE CREDITS</span>
                    </span>
                    <span className="xl:hidden">
                      Annual plan,<br />Paid annually<br />
                      <span className="inline-block mt-1 px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">+2 MONTHS FREE CREDITS</span>
                    </span>
                  </span>
                </div>

                <div className="rounded-xl border border-zinc-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-zinc-200 bg-white">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Plan</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider font-bold">Price</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Credits/mo</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Cost/Credit</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200">
                        {Object.entries(plans).map(([planKey, plan]) => {
                          const displayPrice = isAnnual ? plan.annualPrice : plan.monthlyPrice;
                          const displayCredits = isAnnual ? plan.annualCredits : plan.monthlyCredits;
                          const costPerCredit = calculateCostPerCredit(displayPrice, displayCredits);
                          
                          return (
                            <tr 
                              key={planKey}
                              onClick={() => setSelectedPlan(planKey)} 
                              className={`transition-colors ${
                                selectedPlan === planKey 
                                  ? 'bg-purple-50' 
                                  : 'cursor-pointer hover:bg-purple-50/50'
                              }`}
                            >
                              <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                                selectedPlan === planKey ? 'text-purple-800' : 'text-zinc-900'
                              }`}>
                                <div className="flex items-center">
                                  <span>{plan.name}</span>
                                  {planKey === 'growth' && <span className="ml-2 text-xs font-semibold text-emerald-700">(Popular)</span>}
                                </div>
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-lg font-bold ${
                                selectedPlan === planKey ? 'text-purple-800' : 'text-zinc-900'
                              }`}>
                                {planKey === 'enterprise' ? 'Contact Sales' : (
                                  <>
                                    {displayPrice}
                                    {isAnnual ? <span className="text-sm font-normal text-zinc-500 ml-1">/ yr</span> : <span className="text-sm font-normal text-zinc-500 ml-1">/ mo</span>}
                                  </>
                                )}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                                selectedPlan === planKey ? 'text-purple-800 font-medium' : 'text-zinc-900'
                              }`}>
                                {planKey === 'enterprise' ? 'Custom' : displayCredits}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                                selectedPlan === planKey ? 'text-purple-800 font-medium' : 'text-zinc-900'
                              }`}>
                                {planKey === 'enterprise' ? 'Contact Sales' : `$${costPerCredit}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="rounded-xl border border-zinc-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-zinc-200 bg-white">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Credits</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Cost/Credit</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider font-bold">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200">
                        {Object.entries(paygPlans).map(([key, plan]) => {
                          const costPerCredit = calculateCostPerCredit(plan.price, plan.credits);
                          
                          return (
                            <tr 
                              key={key}
                              onClick={() => setSelectedPayg(key)}
                              className={`cursor-pointer hover:bg-purple-50/50 transition-colors ${
                                selectedPayg === key ? 'bg-purple-50' : ''
                              }`}
                            >
                              <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                                selectedPayg === key ? 'text-purple-800' : 'text-zinc-900'
                              }`}>
                                {plan.credits}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                                selectedPayg === key ? 'text-purple-700' : 'text-zinc-600'
                              }`}>
                                ${costPerCredit}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-lg font-bold ${
                                selectedPayg === key ? 'text-purple-800' : 'text-zinc-900'
                              }`}>
                                {plan.price}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Plan Details */}
        <div className="bg-zinc-50/50 lg:w-1/3 flex">
          <div className="flex flex-col w-full min-h-full p-8">
            <div className="flex-1 pb-6">
              {/* Subscription Content */}
              {activeTab === 'subscription' ? (
                <div>
                  <div className="text-center">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 mb-6">
                      {currentPlan.name}
                    </div>
                    <div className="flex flex-col items-center mb-2">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-zinc-900">{currentPlan.displayPrice}</span>
                        {currentPlan.displayPrice !== 'Contact Sales' && (
                          <span className="ml-1 text-lg text-zinc-600">{isAnnual ? '/ yr' : '/ mo'}</span>
                        )}
                      </div>
                      <span className="text-sm text-zinc-600 mt-1">
                        {currentPlan.displayPrice === 'Contact Sales'
                          ? 'Custom enterprise pricing'
                          : isAnnual ? 'Annual plan, paid annually' : 'Annual plan, paid monthly'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3 mt-8">
                    <div className="flex items-center text-zinc-700">
                      <i className="ph-bold ph-coin w-5 h-5 mr-3 text-purple-500 flex-shrink-0"></i>
                      <span className="text-sm font-medium">
                        {currentPlan.displayCredits === 'Custom' ? 'Custom credit volume' : `${currentPlan.displayCredits} credits`}
                        {isAnnual && currentPlan.displayCredits !== 'Custom' && currentPlan.monthlyCredits !== 'Custom' && (
                          <span className="text-emerald-600 ml-1">
                            (+{Math.floor(parseInt(currentPlan.monthlyCredits.replace(/,/g, '')) * 0.2).toLocaleString()} bonus)
                          </span>
                        )}
                        {currentPlan.displayCredits !== 'Custom' && (
                          <span className="text-zinc-500 font-normal"> {isAnnual ? '/ yr' : '/ mo'}</span>
                        )}
                      </span>
                    </div>

                    <div className="border-t border-zinc-200 pt-3">
                      <h5 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3">What's included</h5>
                      {(() => {
                        const COSTS = (window.PublicPricingConfig && window.PublicPricingConfig.CONFIG.CREDIT_COSTS) || {};
                        const isEnterprise = selectedPlan === 'enterprise';
                        const periodSuffix = isAnnual ? ' / yr' : ' / mo';
                        const cap = (cost) => isEnterprise
                          ? 'Custom'
                          : `~${calculateCapabilityUses(currentPlan.displayCredits, cost)}`;
                        const rateLimit = isEnterprise
                          ? 'Custom'
                          : `${PUBLIC_FREE_API_RATE_LIMITS[selectedPlan] || PUBLIC_FREE_API_RATE_LIMIT_DEFAULT} req/min`;
                        const rows = [
                          [COSTS.CUSTOMER_LISTING_PER_RESULT || 2, 'customer rows'],
                          [COSTS.COMPANY_DETAILS || 3, 'company profiles'],
                          [COSTS.COMPANY_FUNDING_BASE || 2, 'company funding lookups'],
                          [COSTS.COMPANY_UPDATES || 2, 'company update fetches'],
                          [COSTS.COMPANY_SEARCH || 2, 'company searches'],
                          [COSTS.COMPETITOR_LISTING_MIN || 5, 'competitor lookups'],
                          [COSTS.PERSON_PROFILE || 3, 'person profiles'],
                          [COSTS.EMPLOYEE_SEARCH_BASE || 2, 'employee searches'],
                          [COSTS.WORK_EMAIL_HIT || 2, 'work-email lookups']
                        ];
                        return (
                          <div className="space-y-2">
                            {rows.map(([cost, label]) => (
                              <div key={label} className="flex items-center text-sm text-zinc-700">
                                <i className="ph-bold ph-check w-4 h-4 mr-3 text-green-500 flex-shrink-0"></i>
                                <span>{cap(cost)} {label}{periodSuffix}</span>
                              </div>
                            ))}
                            <div className="flex items-center text-sm text-zinc-700">
                              <i className="ph-bold ph-gauge w-4 h-4 mr-3 text-blue-500 flex-shrink-0"></i>
                              <span>Free endpoint rate limit: <strong>{rateLimit}</strong></span>
                            </div>
                            <div className="flex items-center text-sm text-zinc-700">
                              <i className="ph-bold ph-envelope-simple w-4 h-4 mr-3 text-purple-500 flex-shrink-0"></i>
                              <span>
                                Supportive for Gmail
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full uppercase tracking-wider">Included</span>
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-zinc-700">
                              <i className="ph-bold ph-headset w-4 h-4 mr-3 text-green-500 flex-shrink-0"></i>
                              <span>{isEnterprise ? 'Priority support' : 'Standard support'}</span>
                            </div>
                            <div className="flex items-start text-sm text-zinc-700">
                              <i className="ph-bold ph-warning w-4 h-4 mr-3 mt-0.5 text-amber-500 flex-shrink-0"></i>
                              <span>Credits expire at the end of each billing period</span>
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed pt-1">
                              Credits are shared across products - each row is the maximum if you spent every credit on that one product.
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-center">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 mb-6">
                      Pay As You Go
                    </div>
                    <div className="flex flex-col items-center mb-2">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-zinc-900">{currentPlan.price}</span>
                      </div>
                      <span className="text-sm text-zinc-600 mt-1">One-time payment · {currentPlan.validity || '90 days'} validity</span>
                    </div>
                  </div>
                  <div className="space-y-3 mt-8">
                    <div className="flex items-center text-zinc-700">
                      <i className="ph-bold ph-coin w-5 h-5 mr-3 text-purple-500 flex-shrink-0"></i>
                      <span className="text-sm font-medium">{currentPlan.credits} credits total</span>
                    </div>

                    <div className="border-t border-zinc-200 pt-3">
                      <h5 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3">What's included</h5>
                      {(() => {
                        const COSTS = (window.PublicPricingConfig && window.PublicPricingConfig.CONFIG.CREDIT_COSTS) || {};
                        const cap = (cost) => `~${calculateCapabilityUses(currentPlan.credits, cost)}`;
                        const rows = [
                          [COSTS.CUSTOMER_LISTING_PER_RESULT || 2, 'customer rows'],
                          [COSTS.COMPANY_DETAILS || 3, 'company profiles'],
                          [COSTS.COMPANY_FUNDING_BASE || 2, 'company funding lookups'],
                          [COSTS.COMPANY_UPDATES || 2, 'company update fetches'],
                          [COSTS.COMPANY_SEARCH || 2, 'company searches'],
                          [COSTS.COMPETITOR_LISTING_MIN || 5, 'competitor lookups'],
                          [COSTS.PERSON_PROFILE || 3, 'person profiles'],
                          [COSTS.EMPLOYEE_SEARCH_BASE || 2, 'employee searches'],
                          [COSTS.WORK_EMAIL_HIT || 2, 'work-email lookups']
                        ];
                        return (
                          <div className="space-y-2">
                            {rows.map(([cost, label]) => (
                              <div key={label} className="flex items-center text-sm text-zinc-700">
                                <i className="ph-bold ph-check w-4 h-4 mr-3 text-green-500 flex-shrink-0"></i>
                                <span>{cap(cost)} {label} per pack</span>
                              </div>
                            ))}
                            <div className="flex items-center text-sm text-zinc-700">
                              <i className="ph-bold ph-clock w-4 h-4 mr-3 text-blue-500 flex-shrink-0"></i>
                              <span>{currentPlan.validity || '90 days'} validity</span>
                            </div>
                            <div className="flex items-center text-sm text-zinc-700">
                              <i className="ph-bold ph-gauge w-4 h-4 mr-3 text-blue-500 flex-shrink-0"></i>
                              <span>Free endpoint rate limit: <strong>{PUBLIC_FREE_API_RATE_LIMIT_DEFAULT} req/min</strong></span>
                            </div>
                            <div className="flex items-center text-sm text-zinc-500">
                              <i className="ph-bold ph-x-circle w-4 h-4 mr-3 text-zinc-400 flex-shrink-0"></i>
                              <span>
                                Supportive for Gmail
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-zinc-200 text-zinc-600 rounded-full uppercase tracking-wider">Subscription only</span>
                              </span>
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed pt-1">
                              Credits are shared across products - each row is the maximum if you spent every credit on that one product.
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <a href="/auth/register" className="block w-full px-4 py-3 text-sm font-medium text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors mt-8 lg:mt-auto text-center">
              Start Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Main Public Pricing App ====================

const PublicPricingApp = () => {
  // Get configuration
  const config = window.PublicPricingConfig;
  const plans = config.getPlans();
  const paygPlans = config.getPaygPlans();
  const uiConstants = config.getUIConstants();

  // State management
  const [activeTab, setActiveTab] = useState('subscription');
  const [selectedPlan, setSelectedPlan] = useState(uiConstants.DEFAULT_SELECTED_PLAN);
  const [selectedPayg, setSelectedPayg] = useState(uiConstants.DEFAULT_SELECTED_PAYG);
  const [isAnnual, setIsAnnual] = useState(uiConstants.DEFAULT_IS_ANNUAL);

  return (
    <div className="max-w-7xl mx-auto mt-16">
      <div className="max-w-5xl mx-auto">
        <PublicPlansSection
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          selectedPayg={selectedPayg}
          setSelectedPayg={setSelectedPayg}
          isAnnual={isAnnual}
          setIsAnnual={setIsAnnual}
          plans={plans}
          paygPlans={paygPlans}
        />
      </div>
    </div>
  );
};

// Function to mount the React app
function mountReactApp() {
  console.log('Attempting to mount React app');
  
  // Check if React is available
  if (typeof React === 'undefined') {
    console.error('React is not loaded!');
    return false;
  }
  
  if (typeof ReactDOM === 'undefined') {
    console.error('ReactDOM is not loaded!');
    return false;
  }
  
  // Check if PublicPricingConfig is available
  if (typeof window.PublicPricingConfig === 'undefined') {
    console.error('PublicPricingConfig is not loaded!');
    return false;
  }
  
  const root = document.getElementById('pricing-react-root');
  if (root) {
    console.log('Found root element, mounting app');
    try {
      // Use React 18 createRoot API if available, fallback to ReactDOM.render
      if (ReactDOM.createRoot) {
        console.log('Using React 18 createRoot API');
        const reactRoot = ReactDOM.createRoot(root);
        reactRoot.render(<PublicPricingApp />);
      } else {
        console.log('Using legacy ReactDOM.render API');
        ReactDOM.render(<PublicPricingApp />, root);
      }
      console.log('React app mounted successfully');
      return true;
    } catch (error) {
      console.error('Error mounting React app:', error);
      return false;
    }
  } else {
    console.error('Could not find pricing-react-root element');
    return false;
  }
}

// Try to mount when DOM is ready, with retry mechanism
let retryCount = 0;
const maxRetries = 50; // 5 seconds max

function initializeApp() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
    return;
  }
  
  console.log('DOM ready, checking if we can mount app', `(attempt ${retryCount + 1})`);
  
  // If React is not available yet, wait a bit and try again
  if (typeof React === 'undefined' || typeof ReactDOM === 'undefined') {
    if (retryCount < maxRetries) {
      console.log('React not ready yet, retrying in 100ms...');
      retryCount++;
      setTimeout(initializeApp, 100);
      return;
    } else {
      console.error('React failed to load after maximum retries');
      showFallbackMessage('React libraries failed to load');
      return;
    }
  }
  
  // If config is not available yet, wait a bit and try again
  if (typeof window.PublicPricingConfig === 'undefined') {
    if (retryCount < maxRetries) {
      console.log('PublicPricingConfig not ready yet, retrying in 50ms...');
      retryCount++;
      setTimeout(initializeApp, 50);
      return;
    } else {
      console.error('PublicPricingConfig failed to load after maximum retries');
      showFallbackMessage('Pricing configuration failed to load');
      return;
    }
  }
  
  if (!mountReactApp()) {
    showFallbackMessage('Failed to mount pricing table');
  }
}

// Show fallback message if React fails
function showFallbackMessage(reason) {
  const root = document.getElementById('pricing-react-root');
  if (root) {
    root.innerHTML = `
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <div class="text-yellow-800 mb-2">
          <svg class="mx-auto h-8 w-8 mb-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-yellow-800 mb-2">Pricing Table Loading Issue</h3>
        <p class="text-yellow-700 text-sm mb-4">${reason}. Please refresh the page or contact support if the issue persists.</p>
        <button onclick="location.reload()" class="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
          Refresh Page
        </button>
      </div>
    `;
  }
}

// Start the initialization
initializeApp();
